import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { forwardRef, useCheckboxGroupContext, useMultiStyleConfig, omitThemingProps, useCheckbox, chakra } from '@chakra-ui/react';
import React__default from 'react';
import { callAll, cx } from '@chakra-ui/utils';
import { CheckboxControl } from './CheckboxControl.js';
import { Label } from './Label.js';
import { Check } from '../../icons/Check.js';
var _excluded = ["spacing", "className", "children", "iconColor", "iconSize", "icon", "isChecked", "isDisabled", "onChange", "inputProps"];
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}

/**
 * Checkbox
 *
 * React component used in forms when a user needs to select
 * multiple values from several options.
 *
 * @see Docs https://chakra-ui.com/checkbox
 */
var Checkbox = forwardRef(function (props, ref) {
  var group = useCheckboxGroupContext();
  var mergedProps = _objectSpread(_objectSpread({}, group), props);
  var styles = useMultiStyleConfig('Checkbox', mergedProps);
  var ownProps = omitThemingProps(props);
  var _ownProps$spacing = ownProps.spacing,
    spacing = _ownProps$spacing === void 0 ? '0.5rem' : _ownProps$spacing,
    className = ownProps.className,
    children = ownProps.children,
    iconColor = ownProps.iconColor,
    iconSize = ownProps.iconSize,
    _ownProps$icon = ownProps.icon,
    icon = _ownProps$icon === void 0 ? /*#__PURE__*/React__default.createElement(Check, null) : _ownProps$icon,
    isCheckedProp = ownProps.isChecked,
    _ownProps$isDisabled = ownProps.isDisabled,
    isDisabled = _ownProps$isDisabled === void 0 ? group === null || group === void 0 ? void 0 : group.isDisabled : _ownProps$isDisabled,
    onChangeProp = ownProps.onChange;
  ownProps.inputProps;
  var rest = _objectWithoutProperties(ownProps, _excluded);
  var isChecked = isCheckedProp;
  if (group !== null && group !== void 0 && group.value && ownProps.value) {
    isChecked = group.value.includes(ownProps.value);
  }
  var onChange = onChangeProp;
  if (group !== null && group !== void 0 && group.onChange && ownProps.value) {
    onChange = callAll(group.onChange, onChangeProp);
  }
  var _useCheckbox = useCheckbox(_objectSpread(_objectSpread({}, rest), {}, {
      isDisabled: isDisabled,
      isChecked: isChecked,
      onChange: onChange
    })),
    state = _useCheckbox.state,
    getInputProps = _useCheckbox.getInputProps,
    getCheckboxProps = _useCheckbox.getCheckboxProps,
    getLabelProps = _useCheckbox.getLabelProps,
    getRootProps = _useCheckbox.getRootProps;
  var iconStyles = React__default.useMemo(function () {
    return _objectSpread({
      opacity: state.isChecked || state.isIndeterminate ? 1 : 0,
      transform: state.isChecked || state.isIndeterminate ? 'scale(1)' : 'scale(0.75)',
      fontSize: iconSize,
      color: iconColor
    }, styles.icon);
  }, [iconColor, iconSize, state.isChecked, state.isIndeterminate, styles.icon]);
  var clonedIcon = /*#__PURE__*/React__default.cloneElement(icon, {
    __css: iconStyles
  });
  return /*#__PURE__*/React__default.createElement(Label, _extends({
    __css: styles.container,
    className: cx('chakra-checkbox', className)
  }, getRootProps()), /*#__PURE__*/React__default.createElement("input", _extends({
    className: "chakra-checkbox__input"
  }, getInputProps({}, ref))), /*#__PURE__*/React__default.createElement(CheckboxControl, _extends({
    __css: styles.control,
    className: "chakra-checkbox__control"
  }, getCheckboxProps()), clonedIcon), children && /*#__PURE__*/React__default.createElement(chakra.span, _extends({
    className: "chakra-checkbox__label"
  }, getLabelProps(), {
    __css: _objectSpread({
      marginStart: spacing
    }, styles.label)
  }), children));
});
export { Checkbox };